import { CATALOG_BASE_PATH, IMG_BASE_PATH } from '@/const'
import { AppSupportMenuAiChat } from '@/catalog-apps/components/AppSupportMenuAiChat/AppSupportMenuAiChat'
import { SingleSelectCombobox } from '@/components/form-components/SingleSelectCombobox'
import { useProjectsOrganisationFilter } from '@/pages/Projects/ProjectsOrganisationFilterContext'
import { useUserLinkedOrganisations } from '@/pages/Projects/useUserLinkedProjects'
import { Link, useLocation, useMatch, useNavigate } from 'react-router'
import { useHeader } from './HeaderContext'
import { UserWidget2 } from './UserWidget2'
import { Separator } from '@/components/shadcn/separator'
import { MAIN_HEADER_HEIGHT } from '@/catalog-apps/components/config'
import { ReactComponent as PwcLogo } from '@/assets/svg/pwc__logo_v2.svg'
import { ToggleGroup, ToggleGroupItem } from '@/components/shadcn/toggle-group'
import { useSessionStorage } from '@/hooks'
import { HeaderTitleNavigation } from './HeaderContext'
import { isApplicationAdmin, isPlatformAdmin, isProjectAdmin, isProjectLeader, isSuperUser } from '@/util'
import { ErrorBoundary } from 'react-error-boundary'
import { Suspense } from 'react'

export function Header() {
  const { title1, title2, titleNavigation, rightTitle, coBrandingImageKey } = useHeader()
  const isProjectsHome = useMatch({ path: '/projects', end: true }) != null
  const title = (
    <div className="flex shrink-0 items-center gap-4">
      <span>{title1 ?? 'Data Modelling Platform'}</span>
      {title2 && (
        <>
          <Separator
            orientation="vertical"
            className="inline-block h-4 w-[2px] rounded-b-full rounded-t-full bg-foreground"
          />
          <span>{title2}</span>
        </>
      )}
    </div>
  )

  const Icon = rightTitle?.icon

  const rTitle = rightTitle ? (
    <div className="flex items-center gap-x-1">
      {Icon && <Icon />}
      {rightTitle.text}
    </div>
  ) : null

  return (
    // Container
    <div
      style={{ height: `${MAIN_HEADER_HEIGHT}px` }}
      className="relative z-50 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center bg-background pl-2.5 pr-7 shadow-md"
    >
      {/* Logo & Titles */}
      <div className="flex min-h-0 items-center">
        <Link to="/" className="flex shrink-0 items-center">
          {coBrandingImageKey ? (
            <img
              className="h-[29px] w-auto object-contain"
              alt="Data Modelling Platform"
              src={`${IMG_BASE_PATH}${coBrandingImageKey}`}
            />
          ) : (
            <span className="inline-flex items-center justify-center p-2.5 pr-4">
              <PwcLogo
                className="block aspect-[360/180] h-6 w-12 min-w-[48px] shrink-0 fill-current text-black dark:text-white"
                aria-label="Data Modelling Platform"
                role="img"
              />
            </span>
          )}
        </Link>
        {/* <Separator orientation="vertical" style={{ height: `${MAIN_HEADER_HEIGHT}px` }} /> */}
        <div className="flex translate-y-[0.03125rem] items-center text-[1.1rem] text-foreground">{title}</div>
      </div>
      <div className="justify-self-center">
        <AppWorkspaceToggle titleNavigation={titleNavigation} />
      </div>
      <div className="flex items-center gap-2 justify-self-end text-lg">
        {isProjectsHome && (
          <ErrorBoundary fallback={null}>
            <Suspense fallback={null}>
              <ProjectsHomeOrganisationSelector />
            </Suspense>
          </ErrorBoundary>
        )}
        {rTitle}
        <Separator style={{ height: `${MAIN_HEADER_HEIGHT}px` }} orientation="vertical" />
        <AppSupportMenuAiChatGate>
          <AppSupportMenuAiChat variant="header" />
        </AppSupportMenuAiChatGate>
        <UserWidget2 />
      </div>
    </div>
  )
}

function ProjectsHomeOrganisationSelector() {
  const organisations = useUserLinkedOrganisations()
  const { organisationOptions, selectedOrganisation, setOrganisationFilter } =
    useProjectsOrganisationFilter(organisations)

  if (organisationOptions.length <= 1) return null

  return (
    <SingleSelectCombobox
      options={organisationOptions}
      selectedValue={selectedOrganisation}
      onSelectionChange={(value) => {
        if (value) {
          setOrganisationFilter(value)
        }
      }}
      placeholder="Select organisation"
      searchPlaceholder="Search organisations..."
      emptyMessage="No organisations found."
      allowClear={false}
      className="h-8 w-[220px]"
    />
  )
}

function AppSupportMenuAiChatGate({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  if (!location.pathname.startsWith(`${CATALOG_BASE_PATH}/`)) return null
  return children
}

function AppWorkspaceToggle({ titleNavigation }: { titleNavigation?: HeaderTitleNavigation }) {
  const navigate = useNavigate()
  const [roles] = useSessionStorage('roles', 0)

  const showToggle =
    !!titleNavigation &&
    (isProjectAdmin(roles) ||
      isProjectLeader(roles) ||
      isSuperUser(roles) ||
      isApplicationAdmin(roles) ||
      isPlatformAdmin(roles))

  if (!showToggle) return null

  return (
    <ToggleGroup
      aria-label="Switch between application and workspace"
      className="h-[35px] rounded-md bg-offwhite p-0.5 shadow"
      type="single"
      value={titleNavigation.current}
      onValueChange={(value) => {
        if (value && value !== titleNavigation.current) {
          navigate(titleNavigation.target)
        }
      }}
    >
      <ToggleGroupItem className="h-[32px] px-2 text-xs font-medium" value="application">
        Application
      </ToggleGroupItem>
      <ToggleGroupItem className="h-[32px] px-2 text-xs font-medium" value="workspace">
        Workspace
      </ToggleGroupItem>
    </ToggleGroup>
  )
}
